import { toPercentage, toSignificant } from '@/utilities/formatting';
import DataTable from '@components/DataTable';

interface DiscountRateProps {
  uniqueSymbol: string;
  exchangeCountryISO: string;
  industrySecondaryName: string;
  unleveredBeta: number;
  de: number;
  taxRate: number;
  equityPremium: number;
  riskFreeRate: number;
  leveredBetaActual: number;
  leveredBeta: number;
  costOfEquity: number;
  twoStagefcf?: {
    affoEstimates?: number;
    growthCAGR5y: number;
    scale: string;
    scaleNumeric: number;
    sharesOutstanding: number;
    fcfData: { date: number; value: number }[];
    fcfCount: { date: number; value: number }[];
    fcfLtm: number;
    firstStage: {
      year: number;
      data: number;
      source: string;
      discounted: number;
    }[];
  };
}

const DiscountRate = ({
  uniqueSymbol,
  exchangeCountryISO,
  industrySecondaryName,
  unleveredBeta,
  de,
  taxRate,
  equityPremium,
  riskFreeRate,
  leveredBetaActual,
  leveredBeta,
  costOfEquity,
  twoStagefcf,
}: DiscountRateProps) => (
  <DataTable light>
    <caption>
      {`Calculation of Discount Rate/ Cost of Equity for ${uniqueSymbol}`}
    </caption>
    <thead>
      <tr>
        <th>Data Point</th>
        <th>Calculation/ Source</th>
        <th>Result</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>Risk-Free Rate</td>
        <td>{`5-Year Average of ${exchangeCountryISO} Long-Term Govt Bond Rate`}</td>
        <td>{toPercentage(riskFreeRate, 1)}</td>
      </tr>
      <tr>
        <td>Equity Risk Premium</td>
        <td>S&amp;P Global</td>
        <td>{toPercentage(equityPremium, 1)}</td>
      </tr>
      {twoStagefcf && (
        <tr>
          <td>{`${industrySecondaryName} Unlevered Beta`}</td>
          <td>Simply Wall St/ S&amp;P Global</td>
          <td>{toSignificant(unleveredBeta, 2)}</td>
        </tr>
      )}
      {twoStagefcf && (
        <tr>
          <td>Re-levered Beta</td>
          <td>
            <span>
              = 0.33 + [(0.66 * Unlevered beta) * (1 + (1 - tax rate)
              (Debt/Market Equity))]
            </span>
            <br />
            <span>
              {`= 0.33 + [(0.66 * ${toSignificant(
                unleveredBeta,
                3
              )}) * (1 + (1 - ${toPercentage(taxRate, 1)}) (${toPercentage(
                de,
                2
              )}))]`}
            </span>
          </td>
          <td>{toSignificant(leveredBetaActual, 3)}</td>
        </tr>
      )}
      {!twoStagefcf && (
        <tr>
          <td>{`${industrySecondaryName} Levered Beta`}</td>
          <td>Simply Wall St/ S&P Global</td>
          <td>{toSignificant(leveredBetaActual, 2)}</td>
        </tr>
      )}
      <tr>
        <td>Levered Beta</td>
        <td>
          <span>Levered Beta limited to 0.8 to 2.0</span>
          <br />
          <span>(practical range for a stable firm)</span>
        </td>
        <td>
          {toSignificant(leveredBeta, 1) === '0.8' ||
          toSignificant(leveredBeta, 1) === '2'
            ? toSignificant(leveredBeta, 1)
            : toSignificant(leveredBeta, 3)}
        </td>
      </tr>
      <tr>
        <td>Discount Rate/ Cost of Equity</td>
        <td>
          <span>
            = Cost of Equity = Risk Free Rate + (Levered Beta * Equity Risk
            Premium)
          </span>
          <br />
          <span>{`= ${toPercentage(riskFreeRate, 2)} + (${toSignificant(
            leveredBeta,
            3
          )} * ${toPercentage(equityPremium, 2)})`}</span>
        </td>
        <td>{toPercentage(costOfEquity, 2)}</td>
      </tr>
    </tbody>
  </DataTable>
);

export default DiscountRate;
